/**
 * Sesiones de reproducción concretas. **Browser-only** — usan `Audio`,
 * `AudioContext`, `Blob`, `URL.createObjectURL`. Validación real en F1
 * (Safari iOS, Chrome Android, desktop). Excluido del coverage en
 * `vitest.config.ts`.
 */

import { AudioPlayerError } from './errors.js';
import type { PlayerSession } from './audioPlayer.js';

export function defaultHtmlAudioSessionFactory(
  binary: ArrayBuffer,
  mimeType: string,
): PlayerSession {
  const blob = new Blob([binary], { type: mimeType });
  const url = URL.createObjectURL(blob);

  let resolveCompleted!: () => void;
  let rejectCompleted!: (err: unknown) => void;
  const completed = new Promise<void>((resolve, reject) => {
    resolveCompleted = resolve;
    rejectCompleted = reject;
  });

  let settled = false;
  const settle = (action: () => void) => {
    if (settled) return;
    settled = true;
    action();
  };

  const audio = new Audio();
  audio.src = url;

  const onEnded = () => settle(() => resolveCompleted());
  const onError = () =>
    settle(() =>
      rejectCompleted(new AudioPlayerError('playback_failed', 'HTMLAudioElement raised "error"')),
    );

  audio.addEventListener('ended', onEnded);
  audio.addEventListener('error', onError);

  // En Safari iOS la promesa de `play()` rechaza si no hay gesto. F1
  // garantiza que el primer play ocurre dentro de un handler de tap
  // (`04_ARQUITECTURA.md` §4.3, "Autoplay de audio").
  Promise.resolve()
    .then(() => audio.play())
    .catch((err) =>
      settle(() =>
        rejectCompleted(
          new AudioPlayerError('playback_failed', 'HTMLAudioElement.play() rejected', err),
        ),
      ),
    );

  return {
    completed,
    cancel() {
      audio.pause();
      settle(() => resolveCompleted());
    },
    dispose() {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      URL.revokeObjectURL(url);
    },
  };
}

export function defaultWebAudioSessionFactory(binary: ArrayBuffer): PlayerSession {
  const ctx = new AudioContext();

  let resolveCompleted!: () => void;
  let rejectCompleted!: (err: unknown) => void;
  const completed = new Promise<void>((resolve, reject) => {
    resolveCompleted = resolve;
    rejectCompleted = reject;
  });

  let settled = false;
  const settle = (action: () => void) => {
    if (settled) return;
    settled = true;
    action();
  };

  let source: AudioBufferSourceNode | null = null;

  // `decodeAudioData` puede neutrar el ArrayBuffer en algunos browsers.
  // Pasamos una copia para que el caller pueda reusar `binary` si quiere.
  const copy = binary.slice(0);
  ctx
    .decodeAudioData(copy)
    .then((buffer) => {
      if (settled) return;
      try {
        source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => settle(() => resolveCompleted());
        source.start();
      } catch (err) {
        settle(() =>
          rejectCompleted(
            new AudioPlayerError('playback_failed', 'AudioBufferSourceNode.start() failed', err),
          ),
        );
      }
    })
    .catch((err) => {
      settle(() =>
        rejectCompleted(
          new AudioPlayerError('decode_failed', 'AudioContext.decodeAudioData() failed', err),
        ),
      );
    });

  return {
    completed,
    cancel() {
      if (source) {
        try {
          source.stop();
        } catch {
          // start() todavía no llamado, o ya parado — ignorar.
        }
      }
      settle(() => resolveCompleted());
    },
    dispose() {
      if (source) {
        source.onended = null;
        try {
          source.disconnect();
        } catch {
          // Ya desconectado.
        }
      }
      void ctx.close();
    },
  };
}
